/**
 * PRIMITIVE / PALETTE COLORS (Raw Values)
 * Focused palette with core colors and essential lighter/darker shades used in the app.
 */
export const paletteColors = {
    dbRed: {
        main: '#EC0016', // core DB red
        // MUI calculates the dark and the light variant automatically if not provided
    },
    charcoal: {
        light: '#3C414B', // lighter charcoal / border
        main: '#282D37', // core charcoal
        dark: '#0F1219', // deepest dark / background
    },

    black: '#000000',
    white: '#FFFCF8',
    lightGrey: '#EAE7E5', // warm white mixed with charcoal at 0.1
    warmSurface: '#FAF7F3', // warm white with a subtle tint for drop surfaces
    warmBorder: '#D8D2CB', // soft warm gray for drop surface borders
    editingSurface: '#F2EEEA', // warmSurface with an approximately 4% charcoal.main tint
    disabledSurface: '#D4D3D1', // warm white mixed with charcoal at 0.2 for disabled controls
    grey: '#77797D', // warm white mixed with charcoal at 0.5
    coolGray: '#AFB4BB',
} as const

/**
 * SEMANTIC / UI-FOCUSED THEME TOKENS
 * References raw palette colors by UI role (brand, text, surface, background, border, status).
 * Components and theme definitions consume these semantic tokens.
 */
export const semanticColors = {
    brand: {
        primary: paletteColors.dbRed.main,
        primaryContrastText: paletteColors.white,
        secondary: paletteColors.white,
        secondaryHover: paletteColors.dbRed.main,
        secondaryContrastText: paletteColors.black,
    },
    text: {
        primary: paletteColors.charcoal.main,
        secondary: paletteColors.grey,
        muted: paletteColors.coolGray,
        disabled: paletteColors.grey,
        contrast: paletteColors.white,
    },
    surface: {
        default: paletteColors.white,
        dark: paletteColors.charcoal.main,
        subtle: paletteColors.lightGrey,
        editing: paletteColors.editingSurface,
        dropzone: paletteColors.warmSurface,
        dropzoneBorder: paletteColors.warmBorder,
        disabled: paletteColors.disabledSurface,
    },
    background: {
        default: paletteColors.white,
        dark: paletteColors.charcoal.main,
        darkDefault: paletteColors.charcoal.dark,
    },
    border: {
        default: paletteColors.grey,
        focus: paletteColors.charcoal.main,
        dark: paletteColors.charcoal.light,
    },
    status: {
        info: paletteColors.charcoal.light,
        infoSurface: paletteColors.white,
        infoText: paletteColors.charcoal.light,
        infoIcon: paletteColors.charcoal.light,
    },
} as const

type ColorGroup = Record<string, string>
type ColorTokenTree<T> = { [K in keyof T]: T[K] extends ColorGroup ? { [P in keyof T[K]]: string } : never }

/**
 * Helper: converts a group and key into a CSS variable name to be used in var(--token, fallback).
 */
function toCssVarName(group: string, key: string): string {
    return `--color-${group}-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
}

/**
 * Wraps each semantic color in var(--token, fallback) and collects matching :root declarations.
 * To be used for generating
 * - CSS variable declarations as fallback values for semantic colors
 * - Tokens to use in the application
 * (<tokensVar>.<semanticColorGroup>.<colorKey>), as ex: colorTokens.brand.primary
 * @param colors color object to convert into CSS variables
 * @returns an object containing the generated color tokens and CSS variable declarations
 */
function buildColorTokens<T extends Record<string, ColorGroup>>(colors: T) {
    const tokens = {} as Record<string, Record<string, string>>
    const cssLines: string[] = []

    for (const [group, values] of Object.entries(colors)) {
        const groupTokens: Record<string, string> = {}
        for (const [key, value] of Object.entries(values)) {
            const varName = toCssVarName(group, key)
            groupTokens[key] = `var(${varName}, ${value})`
            cssLines.push(`  ${varName}: ${value};`)
        }
        tokens[group] = groupTokens
    }

    return {
        tokens: tokens as ColorTokenTree<T>,
        cssVariableDeclarations: `\n:root {\n${cssLines.join('\n')}\n}\n`,
    }
}

const { tokens: colorTokens, cssVariableDeclarations } = buildColorTokens(semanticColors)

export { colorTokens, cssVariableDeclarations }
